import { DataTable } from '@components/tables/DataTable'
import {
  Box,
  Chip,
  Container,
  Grid,
  IconButton,
  Paper,
  Typography,
} from '@mui/material'
import { GridColumns, GridRowId } from '@mui/x-data-grid-pro'
import { EditIcon } from 'lucide-react'
import React, { useCallback, useEffect, useState } from 'react'
import { useFunctionalUnit } from '../../hooks/useFunctionalUnit'
import {
  CareTypeEnum,
  FunctionalUnitDTO,
} from '../../services/functionalUnitService'
import { DeleteConfirmationDialog } from '../dialogs/DeleteConfirmationDialog'

const getCareTypeLabel = (careType: CareTypeEnum): string => {
  switch (careType) {
    case CareTypeEnum.Outpatient:
      return 'Ambulatoire'
    case CareTypeEnum.Inpatient:
      return 'Hospitalisation'
    case CareTypeEnum.Ambulatory:
      return 'Ambulatoire'
    default:
      return careType
  }
}

const getCareTypeColor = (
  careType: CareTypeEnum,
):
  | 'default'
  | 'primary'
  | 'secondary'
  | 'error'
  | 'info'
  | 'success'
  | 'warning' => {
  switch (careType) {
    case CareTypeEnum.Outpatient:
      return 'primary'
    case CareTypeEnum.Inpatient:
      return 'secondary'
    case CareTypeEnum.Ambulatory:
      return 'info'
    default:
      return 'default'
  }
}

export const FunctionalUnitList: React.FC<{
  onAdd?: () => void
  onEdit?: (functionalUnit: FunctionalUnitDTO) => void
  onDelete?: (id: number) => void
}> = ({ onAdd, onEdit, onDelete }) => {
  const {
    functionalUnits,
    loading,
    error,
    fetchAll: fetchFunctionalUnits,
    remove,
  } = useFunctionalUnit()
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [selectedRows, setSelectedRows] = useState<number[]>([])

  useEffect(() => {
    fetchFunctionalUnits()
  }, [fetchFunctionalUnits])


  const _onDeleteConfirm = async () => {
    for (const id of selectedRows) {
      await remove(id)
      onDelete?.(id)
    }
    setIsDeleteDialogOpen(false)
    setSelectedRows([])
  }

  const _onSelectionChange = (selection: GridRowId[]) => {
    setSelectedRows(selection as number[])
  }

  const onRefresh = useCallback(() => {
    fetchFunctionalUnits()
  }, [fetchFunctionalUnits])


  const columns: GridColumns = [
    { field: 'id', headerName: 'ID', flex: 0.2 },
    { field: 'name', headerName: 'Nom', flex: 1 },
    { field: 'number', headerName: 'Numéro', flex: 0.5 },
    { field: 'label', headerName: 'Libellé', flex: 1 },
    {
      field: 'site',
      headerName: 'Site',
      flex: 1,
      valueGetter: (params) => params.row.site?.label || '-',
    },
    {
      field: 'careType',
      headerName: 'Type de soins',
      flex: 1,
      renderCell: (params) => (
        <Chip
          label={getCareTypeLabel(params.value)}
          color={getCareTypeColor(params.value)}
          size="small"
        />
      ),
    },
    {
      field: 'isExecutingUnit',
      headerName: 'Unité d\'exécution',
      flex: 1,
      valueGetter: (params) => (params.row.isExecutingUnit ? 'Oui' : 'Non'),
    },
    {
      field: 'isRequestingUnit',
      headerName: 'Unité de demande',
      flex: 1,
      valueGetter: (params) => (params.row.isRequestingUnit ? 'Oui' : 'Non'),
    },
    {
      field: 'functionType',
      headerName: 'Type',
      flex: 1,
    },
    {
      field: 'serviceManagerName',
      headerName: 'Responsable de service',
      flex: 1,
      valueGetter: (params) => params.row.serviceManagerName || '-',
    },
    {
      field: 'actions',
      headerName: 'Actions',
      align: 'right',
      flex: 1,
      renderCell: (params) => (
        <IconButton size="small" onClick={() => onEdit?.(params.row)}>
          <EditIcon />
        </IconButton>
      ),
    },
  ]

  if (error) {
    return (
      <Box p={4}>
        <Typography color="error">
          Erreur lors du chargement des unités fonctionnelles: {error}
        </Typography>
      </Box>
    )
  }

  return (
    <Container maxWidth={false} style={{ height: '87%', padding: 0 }}>
      <Paper elevation={2} style={{ width: '100%', borderRadius: 25 }}>
        <DataTable
          rows={functionalUnits || []}
          columns={columns}
          getRowId={(row) => row.id}
          loading={loading}
          toolBarProps={{
            title: 'Unités fonctionnelles',
            icon: 'fa fa-building',
            onAdd: onAdd,
            onRefresh,
            onDelete: () => {
              selectedRows.length
                ? setIsDeleteDialogOpen(true)
                : alert(
                    'Veuillez sélectionner au moins une unité fonctionnelle',
                  )
            },
          }}
          detailPanelHeight={500}
          checkboxSelection
          onSelectionModelChange={_onSelectionChange}
        />
      </Paper>

      <DeleteConfirmationDialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={_onDeleteConfirm}
        title="Supprimer les unités fonctionnelles"
        description="Êtes-vous sûr de vouloir supprimer les unités fonctionnelles sélectionnées ?"
      />
    </Container>
  )
}
